import {
    Component,
    ElementRef,
    ViewEncapsulation,
    computed,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';

/**
 * Native port of the AngularJS `ftSelect` dropdown directive for the homepage messaging "Quote type"
 * dialog (reproduces the v1 `.ft-dropdown` markup for parity). Open/close via a local `open` flag +
 * outside-click close. Selecting writes `selectedKey = item[key]` (two-way `[(selectedKey)]`) and emits
 * `(change)` so the host can run its sync.
 */
@Component({
    selector: 'ft-select-ng',
    imports: [],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-select.component.html',
    host: {
        '(document:click)': 'onDocumentClick($event)',
    },
})
export class FtSelectComponent {
    /** The option array (v1 `items`). */
    readonly items = input<any[]>([]);
    /** The display property name shown in the toggle + each li (v1 `select-value`). */
    readonly value = input('name');
    /** The key property name written/matched (v1 `select-key` / `selected-item-key`). */
    readonly key = input('id');
    /** The currently-selected key (v1 `source[sourceKey]`); two-way via `selectedKeyChange`. */
    readonly selectedKey = model<any>();
    /** Fired after a selection (the host runs `syncDialogType()`); mirrors no native v1 callback. */
    readonly change = output<any>();
    /** v1 `placeholder` attr (unused on the QuoteType dialog → ''). */
    readonly placeholder = input('');

    protected readonly open = signal(false);

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    /** v1 `selectedItem[value]` — resolve the selected option by key, fall back to placeholder. */
    protected readonly displayText = computed<string>(() => {
        const sel = (this.items() || []).find((i) => i && i[this.key()] === this.selectedKey());
        return (sel && sel[this.value()]) || this.placeholder();
    });

    protected toggle(event: Event): void {
        event.stopPropagation();
        this.open.update((open) => !open);
    }

    protected select(item: any): void {
        this.selectedKey.set(item[this.key()]);
        this.change.emit(item);
        this.open.set(false);
    }

    protected onDocumentClick(event: MouseEvent): void {
        if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
            this.open.set(false);
        }
    }
}
